<script>
import swal from 'sweetalert';
import html2canvas from 'html2canvas';
import M from 'materialize-css';
import { addHours, isAfter, format, parse } from 'date-fns';
import { API, debounce, fetchCart, fetchCartSummary } from 'helpers/api';
import { formatMoney, searchParentElements } from 'helpers/utils';
import { updateAllowableFreight } from 'js/modules/allowable-freight-promo';
import { isProd } from 'config/config';

export default {
    components: {},

    mounted: async function () {
        let errors = [];

        // Hide cart link in the site header
        const cartLink = document.querySelector('.db-nav .db-nav__cart__link');
        if (cartLink) cartLink.style.display = 'none';

        this.$el.querySelector('.ob__container').classList.add('busy');

        // Get user data and list of parts in their return (if they have any)
        const data = await API.briggsReturnOrder.get();
        if (data && !data.error) {
            this.eligible = data.eligible;
            this.orderTotalMultiplier = data.orderMultiplier;
            const memo = this.$el.querySelector('.order-total-memo .memo-text');
            memo.textContent = `Order minimum must be ${this.orderTotalMultiplier}x the return total`;
            this.maxReturnTotal = data.maxReturnTotal;

            if (data.results && Array.isArray(data.results)) this.returnList = data.results;
        }

        if (!this.eligible || data.error) {
            this.$el.querySelector('.ob__container').classList.add('hide');
            this.$el.querySelector('.alert-wrapper').classList.remove('hide');
        } else {
            // Get list of products to populate the list of purchasable products for the modal
            const results = await API.briggsReturnOrder.getProducts();
            if (results && !results.error) this.productData = Array.isArray(results) ? results : [];
            else if (results.error) errors.push(results.error);

            // Get list of inactive cart products for the users return order
            const inactive = await API.cart.getInactive('return');
            if (inactive && !inactive.error && Array.isArray(inactive.retVal)) this.productList = inactive.retVal;
            else if (inactive.error) errors.push(inactive.error);

            // Get a list of active cart products for the users return order
            const { CartItems } = await fetchCart(false, 'return');
            if (CartItems && Array.isArray(CartItems)) {
                for (const item of CartItems) {
                    const productResult = this.productData.filter((p) => p.Product.Id == item.NetSuiteItem.Id);
                    if (productResult && !productResult.error && Array.isArray(productResult)) {
                        let product = productResult[0];
                        product.Product.ActualCost = item.ActualDealerCost;
                        product.Product.CartItemId = item.Id;
                        product.Quantity = item.Quantity;

                        this.productList.push(product);
                    } else if (productResult.error) errors.push(productResult.error);
                }
            }

            if (errors.length) this.toast(`Failed to load products.<br>---<br>${errors.join('<br>')}`, 'red');

            // Set order total label
            let orderTotalLabel = this.$el.querySelector('.order-total-label');
            let labelText = this.totalProductsPrice == 0 ? 'Minimum Reached' : 'Qualifying Order Minimum';
            orderTotalLabel.textContent = labelText;

            this.$el.querySelector('.ob__container').classList.remove('busy');

            this.refreshAllowableFreight();
        }
    },

    data: function () {
        return {
            isProcessing: false,
            errorMessages: [],
            showProductModal: false,
            eligible: false,
            productData: [],
            productSearch: '',
            checkedProductIds: [],
            productQuantities: {},
            returnList: [],
            productList: [],
            partSearchTerm: '',
            partSearchResults: [],
            selectedSearchPart: null,
            showDropDown: false,
            partSearchQuantity: '',
            isSearchInvalid: false,
            isQuantityInvalid: false,
            isSearching: true,
            orderTotalMultiplier: 1,
            maxReturnTotal: 0.0,
        };
    },
    filters: {
        formatMoney: function (value) {
            return formatMoney(value);
        },
    },
    watch: {
        partSearchTerm: debounce(function (newValue) {
            const selectedTerm = this.selectedSearchPart
                ? `${this.selectedSearchPart.Product.ManufacturerCode}, ${this.selectedSearchPart.Product.PartNumber}`
                : null;

            if (newValue === selectedTerm) {
                return;
            }

            this.searchParts(newValue);
        }, 300),
    },

    computed: {
        isProd: function () {
            return isProd;
        },

        filteredProducts: function () {
            const search = this.productSearch.trim().toLowerCase();

            if (!search) {
                return this.productData;
            }

            return this.productData.filter((product) => {
                return product.Product.PartNumber.toLowerCase().includes(search) || product.Product.Description.toLowerCase().includes(search);
            });
        },

        briggsOilProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Briggs & Stratton Branded Oil');
        },

        vanguardOilProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Vanguard 2-Cycle and 4-Cycle Oil');
        },

        maintenanceKitProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Maintenance Kits');
        },

        airFilterProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Air Filters');
        },

        oilFilterProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Oil Filters');
        },

        fuelFilterProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Fuel Filters');
        },

        engineProducts: function () {
            return this.filteredProducts.filter((product) => product.Product.PartNote === 'Engines');
        },

        totalReturnedQuantity: function () {
            return this.returnList.reduce((total, part) => total + part.Quantity, 0);
        },

        totalReturnedPrice: function () {
            return this.returnList.reduce((total, part) => total + part.Product.ActualCost * part.Quantity, 0);
        },

        totalProductsQuantity: function () {
            return this.productList.reduce((total, product) => total + product.Quantity, 0);
        },

        totalProductsPrice: function () {
            let returnTotal = this.totalReturnedPrice;
            let orderTotal = returnTotal * this.orderTotalMultiplier;

            if (this.productList.length) {
                let productTotal = this.productList.reduce((total, product) => total + product.Product.ActualCost * product.Quantity, 0);
                orderTotal = orderTotal - productTotal;
            }

            return orderTotal < 0 ? 0 : orderTotal;
        },

        totalOrderPrice: function () {
            return this.productList.reduce((total, product) => total + product.Product.ActualCost * product.Quantity, 0);
        },
    },

    methods: {
        appClick: function (event) {
            const isInsideSearchInput = searchParentElements(event.target, { type: 'class', value: 'ob__search__input' });
            const isInsideSearchCard = searchParentElements(event.target, { type: 'class', value: 'ob__searchcard' });

            if (!isInsideSearchInput && !isInsideSearchCard) {
                this.showDropDown = false;
            }
        },

        toast: function (message, color = 'green') {
            if (message) {
                const ToastConfig = { displayLength: 10000, html: message, classes: color };
                M.toast(ToastConfig);
            }
        },

        refreshAllowableFreight: async function () {
            const summary = await fetchCartSummary('return');
            updateAllowableFreight({ summary });
        },

        searchParts: async function (term) {
            const trimmedTerm = term ? term.trim() : '';

            if (!trimmedTerm) {
                this.partSearchResults = [];
                return;
            }

            this.isSearching = true;

            const results = await API.briggsReturnOrder.autocomplete(trimmedTerm);

            this.partSearchResults = Array.isArray(results) ? results : [];
            this.isSearching = false;

            if (this.partSearchResults.length) this.showDropDown = true;
        },

        selectSearchPart: function (part) {
            this.selectedSearchPart = part;
            this.partSearchTerm = `${part.Product.ManufacturerCode}, ${part.Product.PartNumber}`;
            this.partSearchResults = [];
            this.showDropDown = false;
            this.isSearchInvalid = false;

            this.$nextTick(() => {
                this.$refs.ob_qty.focus();
            });
        },

        addPart: async function () {
            // default to qty 1 if it's not set so it works like the product modal qty inputs
            if (!this.partSearchQuantity) this.partSearchQuantity = 1;

            const quantity = Number(this.partSearchQuantity);

            this.isSearchInvalid = !this.selectedSearchPart;
            this.isQuantityInvalid = !(quantity >= 1);

            if (this.isSearchInvalid || this.isQuantityInvalid) {
                return;
            }

            this.$el.querySelector('.step-content.step-1 .ob__results').classList.add('busy');
            const part = this.selectedSearchPart.Product;
            const result = await API.briggsReturnOrder.addReturnItem(part.Id, quantity, part.ListPrice);
            this.$el.querySelector('.step-content.step-1 .ob__results').classList.remove('busy');

            if (!result.error && Array.isArray(result)) {
                const item = result[0];
                const existingPart = this.returnList.find((part) => part.Product.Id === item.Product.Id);

                if (existingPart) existingPart.Quantity = item.Quantity;
                else this.returnList.push(item);

                this.selectedSearchPart = null;
                this.partSearchTerm = '';
                this.partSearchQuantity = '';

                this.$refs.ob_search.focus();
            } else this.toast(`Failed to add part.<br>${result.error}`, 'red');
        },

        handleQuantityChange: async function (part) {
            if (part.Quantity <= 0) {
                this.removeFromReturnList(part.Product);
            } else {
                const result = await API.briggsReturnOrder.updateQuantity(part.Product.Id, part.Quantity);
                if (result.error) this.toast(`Failed to update quantity.<br>${result.error}`, 'red');
            }
        },

        removeFromReturnList: async function (product) {
            this.$el.querySelector('.step-content.step-1 .ob__results').classList.add('busy');
            const result = await API.briggsReturnOrder.removeItem(product.Id);
            this.$el.querySelector('.step-content.step-1 .ob__results').classList.remove('busy');

            if (!result.error) this.returnList = this.returnList.filter((part) => part.Product.Id !== product.Id);
            else this.toast(`Failed to remove part.<br>${result.error}`, 'red');
        },

        openProductModal: function () {
            this.checkedProductIds = this.productList.map((product) => product.Product.Id);

            this.productList.forEach((product) => {
                this.$set(this.productQuantities, product.Product.Id, product.Quantity);
            });

            this.showProductModal = true;
        },

        closeProductModal: function () {
            this.showProductModal = false;
            this.checkedProductIds = [];
            this.productSearch = '';
        },

        addSelectedProducts: async function () {
            if (this.isProcessing) {
                return;
            }
            this.isProcessing = true;

            const existingIds = this.productList.map((product) => product.Product.Id);

            this.productList = this.productList.filter((product) => this.checkedProductIds.includes(product.Product.Id));

            this.productList.forEach((product) => {
                product.Quantity = this.productQuantities[product.Product.Id] || 1;
            });

            const productsToAdd = this.productData
                .filter((product) => this.checkedProductIds.includes(product.Product.Id) && !existingIds.includes(product.Product.Id))
                .map((product) => ({ ...product, Quantity: this.productQuantities[product.Product.Id] || 1 }));

            let error = '';
            this.$el.querySelector('#productModal .modal-container').classList.add('busy');

            if (productsToAdd.length) {
                const items = productsToAdd.map((product) => ({
                    productId: product.Product.Id,
                    qty: product.Quantity,
                    note: '',
                }));

                const result = await API.product.getForReturn(items);
                if (!result.error && Array.isArray(result)) {
                    this.productList.push(...result);
                } else error += result.error;
            }

            let orderTotalLabel = this.$el.querySelector('.order-total-label');
            let labelText = this.totalProductsPrice == 0 ? 'Minimum Reached' : 'Qualifying Order Minimum';
            orderTotalLabel.textContent = labelText;

            this.$el.querySelector('#productModal .modal-container').classList.remove('busy');

            if (error === '') this.closeProductModal();
            else this.toast(`Failed to add products.<br>${error}`, 'red');

            this.refreshAllowableFreight();
            this.isProcessing = false;
        },

        removeFromProductList: async function (product) {
            this.$el.querySelector('.step-content.step-2 .ob__results').classList.add('busy');
            const result = await API.cart.delete([product.CartItemId], 'annual-return:removeFromProductList');
            this.$el.querySelector('.step-content.step-2 .ob__results').classList.remove('busy');

            if (!result.error) {
                this.productList = this.productList.filter((part) => part.Product.Id !== product.Id);
                this.refreshAllowableFreight();

                let orderTotalLabel = this.$el.querySelector('.order-total-label');
                let labelText = this.totalProductsPrice == 0 ? 'Minimum Reached' : 'Qualifying Order Minimum';
                orderTotalLabel.textContent = labelText;
            } else this.toast(`Failed to remove product.<br>${result.error}`, 'red');
        },

        handleProductQuantityChange: async function (product) {
            if (product.Quantity <= 0) {
                this.removeFromProductList(product.Product);
            } else {
                const result = await API.cart.updateQuantity(product.Product.CartItemId, product.Quantity);
                if (!result.error) {
                    const item = this.productList.find((part) => part.Product.Id === product.Product.Id);
                    item.Quantity = result.Quantity;
                    this.refreshAllowableFreight();
                } else this.toast(`Failed to update quantity.<br>${result.error}`, 'red');
            }
        },

        goToStep1: function () {
            this.$el.querySelector('.step-block.active').classList.remove('active');
            this.$el.querySelector('.step-block.step-1').classList.remove('done');
            this.$el.querySelector('.step-block.step-2').classList.remove('done');
            this.$el.querySelector('.step-block.step-3').classList.remove('done');
            this.$el.querySelector('.step-block.step-1').classList.add('active');

            this.$el.querySelector('.step-content.step-active').classList.remove('step-active');
            this.$el.querySelector('.step-content.step-1').classList.add('step-active');
        },

        goToStep2: function () {
            if (!this.returnList.length) {
                this.toast('Please add at least 1 part to your return.', 'red');
                return;
            }

            // I added the "this.maxReturnTotal !== 0" for testing locally (not sure if it should remain here)
            if (this.maxReturnTotal !== 0 && this.totalReturnedPrice > this.maxReturnTotal) {
                let maxTotal = formatMoney(this.maxReturnTotal);
                this.toast(`Your return total must be less than ${maxTotal}`, 'red');
                return;
            }

            this.$el.querySelector('.step-block.active').classList.remove('active');
            this.$el.querySelector('.step-block.step-1').classList.add('done');
            this.$el.querySelector('.step-block.step-2').classList.remove('done');
            this.$el.querySelector('.step-block.step-3').classList.remove('done');
            this.$el.querySelector('.step-block.step-2').classList.add('active');

            this.$el.querySelector('.step-content.step-active').classList.remove('step-active');
            this.$el.querySelector('.step-content.step-2').classList.add('step-active');
        },

        goToStep3: function () {
            if (this.totalProductsPrice > 0) {
                this.toast(`Your order total must be at least ${this.orderTotalMultiplier}x your return total.`, 'red');
                return;
            }

            this.$el.querySelector('.step-block.active').classList.remove('active');
            this.$el.querySelector('.step-block.step-1').classList.add('done');
            this.$el.querySelector('.step-block.step-2').classList.add('done');
            this.$el.querySelector('.step-block.step-3').classList.remove('done');
            this.$el.querySelector('.step-block.step-3').classList.add('active');

            this.$el.querySelector('.step-content.step-active').classList.remove('step-active');
            this.$el.querySelector('.step-content.step-3').classList.add('step-active');
        },

        goToStep4: function () {
            this.$el.querySelector('.step-block.active').classList.remove('active');
            this.$el.querySelector('.step-block.step-1').classList.add('done');
            this.$el.querySelector('.step-block.step-2').classList.add('done');
            this.$el.querySelector('.step-block.step-3').classList.add('done');
            this.$el.querySelector('.step-block.step-4').classList.add('active');

            this.$el.querySelector('.step-content.step-active').classList.remove('step-active');
            this.$el.querySelector('.step-content.step-4').classList.add('step-active');
        },

        goToCheckout: async function () {
            const items = this.productList.map((product) => ({
                cartItemId: product.Product.CartItemId,
                qty: product.Quantity,
            }));

            this.$el.querySelector('.step-content.step-4').classList.add('busy');
            const result = await API.cart.add(items);
            this.$el.querySelector('.step-content.step-4').classList.remove('busy');

            if (!result.error) window.location = isProd ? '/portal/checkout/shipping-and-billing?type=return' : '/shipping-billing.html?type=return';
            else this.toast(`Failed to proceed to checkout.<br>${result.error}`, 'red');
        },
    },
};
</script>

<template>
    <section class="annual-return" ref="document" v-on:click="appClick">
        <div class="dashboard__modules">
            <div class="dashboard__modules-wrapper">
                <div class="ob__header">
                    <h2 class="ob__header__title">Annual Stock Adjustment</h2>
                </div>

                <div class="alert-wrapper hide">
                    <div class="alert alert-error">
                        <p><strong>Not Eligible</strong></p>

                        <p>Your account is not currently eligible for the Annual Stock Adjustment or this feature has been disabled.</p>
                    </div>

                    <a href="/portal/dashboard" class="button--orange">Return to Dashboard</a>
                </div>

                <div class="ob__container">
                    <div class="ob__level">
                        <div class="step-container">
                            <div class="step-block step-1 active" @click="goToStep1">
                                <div class="step-number">1</div>
                                Build Return
                            </div>
                            <span class="step-separator"></span>
                            <div class="step-block step-2" @click="goToStep2">
                                <div class="step-number">2</div>
                                Build Order
                            </div>
                            <span class="step-separator"></span>
                            <div class="step-block step-3" @click="goToStep3">
                                <div class="step-number">3</div>
                                Review Return
                            </div>
                            <span class="step-separator"></span>
                            <div class="step-block step-4" @click="goToStep4">
                                <div class="step-number">4</div>
                                Review Order
                            </div>
                        </div>
                    </div>

                    <!--STEP 1-->
                    <div class="ob__level step-content step-1 step-active">
                        <p class="ob__level__title">Build Your Return</p>
                        <p class="ob__level__subtitle">
                            Select the parts you would like to return by searching for parts by part number in the textbox below. Once you have added 1 or more
                            parts, click the "Next" button at the bottom of the page to proceed to the next step.
                            <br /><br />
                            You can download the list of returnable parts
                            <a href="https://dev.powerdistributors.com/docs/default-source/returns/returnable_parts_list.xlsx" target="_blank">here</a>.
                        </p>

                        <div class="order-builder-search">
                            <div class="ob__search return-search">
                                <p class="search-info">Enter the part number you wish to return in the following textbox:</p>
                                <div class="ob__search__wrapper">
                                    <div class="ob__search__part">
                                        <label class="ob__search__label" for="part">Part #</label>
                                        <div class="ob__search__input-wrapper">
                                            <input
                                                class="ob__search__input"
                                                type="text"
                                                name="part"
                                                id="search-box"
                                                placeholder="Enter Part #"
                                                ref="ob_search"
                                                :class="{ invalid: isSearchInvalid }"
                                                v-model="partSearchTerm"
                                                v-on:focus="
                                                    showDropDown = true;
                                                    isSearchInvalid = false;
                                                "
                                                autocomplete="off"
                                            />
                                            <div class="ob__search__dropdown">
                                                <div
                                                    class="ob__search__dropdown-wrapper ob__search__dropdown-wrapper--active"
                                                    v-if="showDropDown && partSearchTerm.trim()"
                                                >
                                                    <div class="no-results" v-if="!isSearching && !partSearchResults.length">
                                                        <span class="no-results-found">This item is not eligible for the Annual Stock Adjustment.</span>
                                                    </div>
                                                    <div
                                                        class="ob__searchcard"
                                                        v-for="(part, i) in partSearchResults"
                                                        :key="i"
                                                        v-on:click="selectSearchPart(part)"
                                                    >
                                                        <p class="ob__searchcard__title">
                                                            <span>{{ part.Product.ManufacturerCode }},</span>
                                                            <span class="ob__searchcard__focus">{{ part.Product.PartNumber }}</span>
                                                        </p>
                                                        <p class="ob__searchcard__description">{{ part.Product.Description }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="ob__search__qty">
                                        <label class="ob__search__label" for="qty">Qty.</label>
                                        <input
                                            id="qty"
                                            type="text"
                                            placeholder="Qty."
                                            ref="ob_qty"
                                            :class="{ invalid: isQuantityInvalid }"
                                            v-model="partSearchQuantity"
                                            v-on:focus="isQuantityInvalid = false"
                                            v-on:keyup.enter="addPart"
                                        />
                                    </div>
                                    <a class="ob__search__btn" v-on:click="addPart">Add Part</a>
                                </div>
                                <div class="return-total">
                                    <p class="ob__search__total-info">
                                        Return Total:
                                        <span class="ob__search--bold">{{ totalReturnedPrice | formatMoney }}</span>
                                    </p>
                                    <small>Maximum Return Total: {{ maxReturnTotal | formatMoney }}</small>
                                </div>
                            </div>
                        </div>

                        <div class="ob__results">
                            <div class="ob__results__header">
                                <p class="ob__results__header-title">Part #</p>
                                <p class="ob__results__header-title ob--tc">Qty.</p>
                                <p class="ob__results__header-title ob--tc">&nbsp;</p>
                                <p class="ob__results__header-title ob__results--flex3">Description</p>
                                <p class="ob__results__header-title ob--tc">List</p>
                                <p class="ob__results__header-title ob--tc">Cost</p>
                                <p class="ob__results__header-title ob--tc">Total</p>
                                <p class="ob__results__header-title checkout__results__header-title"></p>
                            </div>
                            <ul class="ob__results__items js--ob-cart">
                                <li class="js--ob-no-items ob__results__no-items" v-if="returnList.length < 1">
                                    <div class="ob__results__item">
                                        <p class="js--no-items">No parts selected</p>
                                    </div>
                                </li>
                                <li v-for="part in returnList" :key="part.Product.Id" class="ob__results_li">
                                    <div class="ob__results__item">
                                        <div class="ob__results__li ob--justify-start" data-label="Part #">{{ part.Product.PartNumber }}</div>
                                        <div class="ob__results__li ob--tc" data-label="Qty.">
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                tabindex="-1"
                                                class="ob--center"
                                                v-model.number="part.Quantity"
                                                @change="handleQuantityChange(part)"
                                            />
                                        </div>
                                        <div class="ob__results__li ob__results__center-result">&nbsp;</div>
                                        <div class="ob__results__li ob__results--flex3 ob--justify-start" data-label="Description">
                                            {{ part.Product.Description }}
                                        </div>
                                        <div class="ob__results__li" data-label="List">
                                            {{ part.Product.ListPrice | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Cost">
                                            {{ part.Product.ActualCost | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Total">
                                            {{ (part.Product.ActualCost * part.Quantity) | formatMoney }}
                                        </div>
                                        <div class="ob__results__li ob--justify-end">
                                            <button class="ob__results__remove-btn" @click="removeFromReturnList(part.Product)">
                                                <i class="fas fa-times" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="step-button-container">
                            <span
                                >Total parts: <strong>{{ totalReturnedQuantity }}</strong></span
                            >
                            <button id="btnStep2" class="button--orange" @click="goToStep2">Next</button>
                        </div>
                    </div>

                    <!--STEP 2-->
                    <div class="ob__level step-content step-2">
                        <p class="ob__level__title">Build Your Order</p>
                        <p class="ob__level__subtitle">
                            Select any eligible products to add to your order. The selected products must be at or above 2x (for Elite) or 3x (for Preferred)
                            the return order total value. Once complete, click the "Next" button at the bottom of the page to go to the final step.
                        </p>

                        <div class="ob__level js--allowable-freight">
                            <p class="ob__level__title">Allowable Freight Deduction Progress</p>
                            <p class="ob__level__subtitle">
                                Earn an allowable freight cost deduction when the order invoice is paid according to freight policy guidelines
                            </p>
                            <div class="ob__level__percent-wrapper">
                                <div class="ob__level__percent js--ob-percent" data-allowable-freight="">
                                    <i class="fas fa-truck"></i>
                                </div>
                            </div>

                            <div class="ob__level__info">
                                <p class="ob__level__shipping">
                                    <span class="js--shipping-small">Small Parcel Allowable Freight Deduction:</span>
                                    <span class="js--shipping-ltl ob__level--hide">Truck (LTL) Allowable Freight Deduction:</span>
                                    <span class="ob__level--bold js--shipping-freight ob__level__amount"></span>minimum qualifying order
                                </p>
                                <p class="ob__level__description js--ob-af-amount">
                                    Add <span class="ob__level--bold js--ob-amount-left"></span>in qualifying products for Allowable Freight!
                                </p>
                                <p class="ob__level__description js--ob-af-achieved ob__level--hide">You have achieved Allowable Freight!</p>
                            </div>
                            <p class="ob__level__ltl-title js--ltl ob__level--hide"><i class="fas fa-info-circle"></i>Your order may ship via Truck (LTL)</p>
                            <p class="ob__level-ltl-info js--ltl ob__level--hide">
                                Orders containing non-small parcel shipment eligible items or weighing more than the maximum small parcel shipment weight
                                allowance are required to ship via Truck (LTL).
                            </p>
                        </div>

                        <div class="order-builder-search">
                            <div class="ob__search">
                                <p>Click the "Select Products" button to view a list of eligible products you can add to your order.</p>
                                <div class="ob__search__btn__wrapper">
                                    <button class="ob__search__btn" @click="openProductModal">Select Products</button>
                                </div>
                                <div class="order-total">
                                    <span class="fa fa-info-circle"></span>
                                    <div class="order-total-memo"><span class="memo-text"></span> <span class="dn-angle"></span></div>
                                    <p class="ob__search__total-info">
                                        <span class="order-total-label">Qualifying Order Minimum</span>:
                                        <span class="ob__search--bold">{{ totalProductsPrice | formatMoney }}</span>
                                    </p>
                                    <small>Return Total: {{ totalReturnedPrice | formatMoney }}</small>
                                </div>
                            </div>
                        </div>

                        <div class="ob__results">
                            <div class="ob__results__header">
                                <p class="ob__results__header-title">Part #</p>
                                <p class="ob__results__header-title ob--tc">Qty.</p>
                                <p class="ob__results__header-title ob--tc">&nbsp;</p>
                                <p class="ob__results__header-title ob__results--flex3">Description</p>
                                <p class="ob__results__header-title ob--tc">List</p>
                                <p class="ob__results__header-title ob--tc">Cost</p>
                                <p class="ob__results__header-title ob--tc">Total</p>
                                <p class="ob__results__header-title checkout__results__header-title"></p>
                            </div>
                            <ul class="ob__results__items js--ob-cart">
                                <li class="js--ob-no-items ob__results__no-items" v-if="productList.length < 1">
                                    <div class="ob__results__item">
                                        <p class="js--no-items">No products selected</p>
                                    </div>
                                </li>
                                <li v-for="product in productList" :key="product.Product.Id" class="ob__results_li">
                                    <div class="ob__results__item">
                                        <div class="ob__results__li ob--justify-start" data-label="Part #">{{ product.Product.PartNumber }}</div>
                                        <div class="ob__results__li ob--tc" data-label="Qty.">
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                tabindex="-1"
                                                class="ob--center"
                                                v-model.number="product.Quantity"
                                                @change="handleProductQuantityChange(product)"
                                            />
                                        </div>
                                        <div class="ob__results__li ob__results__center-result">&nbsp;</div>
                                        <div class="ob__results__li ob__results--flex3 ob--justify-start" data-label="Description">
                                            {{ product.Product.Description }}
                                        </div>
                                        <div class="ob__results__li" data-label="List">
                                            {{ product.Product.ListPrice | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Cost">
                                            {{ product.Product.ActualCost | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Total">
                                            {{ (product.Product.ActualCost * product.Quantity) | formatMoney }}
                                        </div>
                                        <div class="ob__results__li ob--justify-end">
                                            <button class="ob__results__remove-btn" @click="removeFromProductList(product.Product)">
                                                <i class="fas fa-times" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="step-button-container">
                            <span
                                >Total parts: <strong>{{ totalProductsQuantity }}</strong></span
                            >
                            <div>
                                <button id="btnStep1" class="button--border" @click="goToStep1">Back</button>
                                <button id="btnStep3" class="button--orange" @click="goToStep3">Next</button>
                            </div>
                        </div>
                    </div>

                    <!--STEP 3-->
                    <div class="ob__level step-content step-3">
                        <p class="ob__level__title">Review Your Returns</p>
                        <p class="ob__level__subtitle">
                            Review your returned parts below. If everything looks correct you can hit the "Next" button to review your ordered products.
                        </p>

                        <div class="summary-header">
                            <h3>Returned Parts</h3>

                            <div>
                                Return Total:
                                <strong>{{ totalReturnedPrice | formatMoney }}</strong>
                            </div>
                        </div>

                        <div class="ob__results summary-results">
                            <div class="ob__results__header checkout__results__header">
                                <p class="ob__results__header-title checkout__results__header-title">Part #</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">Qty.</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">&nbsp;</p>
                                <p class="ob__results__header-title checkout__results__header-title ob__results--flex3">Description</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">List</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">Cost</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">Total</p>
                                <p class="ob__results__header-title checkout__results__header-title"></p>
                            </div>
                            <ul class="ob__results__items js--ob-cart">
                                <li class="js--ob-no-items ob__results__no-items" v-if="returnList.length < 1">
                                    <div class="ob__results__item">
                                        <p class="js--no-items">No parts selected</p>
                                    </div>
                                </li>
                                <li v-for="part in returnList" :key="part.Product.Id" class="ob__results_li">
                                    <div class="ob__results__item">
                                        <div class="ob__results__li ob--justify-start" data-label="Part #">{{ part.Product.PartNumber }}</div>
                                        <div class="ob__results__li ob--tc ob__results__center-result" data-label="Qty.">
                                            {{ part.Quantity }}&nbsp;&nbsp;&nbsp;
                                        </div>
                                        <div class="ob__results__li ob__results__center-result">&nbsp;</div>
                                        <div class="ob__results__li ob__results--flex3 ob--justify-start" data-label="Description">
                                            {{ part.Product.Description }}
                                        </div>
                                        <div class="ob__results__li" data-label="List">
                                            {{ part.Product.ListPrice | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Cost">
                                            {{ part.Product.ActualCost | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Total">
                                            {{ (part.Product.ActualCost * part.Quantity) | formatMoney }}
                                        </div>
                                        <div class="ob__results__li ob--justify-end">&nbsp;</div>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="summary-footer">
                            <span
                                >Total parts: <strong>{{ totalReturnedQuantity }}</strong></span
                            >
                        </div>

                        <div class="step-button-container">
                            <span></span>
                            <div>
                                <button id="btnBackToStep2" class="button--border" @click="goToStep2">Back</button>
                                <button id="btnStep4" class="button--orange" @click="goToStep4">Next</button>
                            </div>
                        </div>
                    </div>

                    <!--STEP 4-->
                    <div class="ob__level step-content step-4">
                        <p class="ob__level__title">Review Your Ordered Products</p>
                        <p class="ob__level__subtitle">
                            Review your ordered products below. If everything looks correct you can hit the "Next" button to procedd to checkout.
                        </p>

                        <div class="summary-header">
                            <h3>Ordered Parts</h3>

                            <div>
                                Order Total:
                                <strong>{{ totalOrderPrice | formatMoney }}</strong>
                            </div>
                        </div>

                        <div class="ob__results summary-results">
                            <div class="ob__results__header checkout__results__header">
                                <p class="ob__results__header-title checkout__results__header-title">Part #</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">Qty.</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">&nbsp;</p>
                                <p class="ob__results__header-title checkout__results__header-title ob__results--flex3">Description</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">List</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">Cost</p>
                                <p class="ob__results__header-title checkout__results__header-title ob--tc">Total</p>
                                <p class="ob__results__header-title checkout__results__header-title"></p>
                            </div>
                            <ul class="ob__results__items js--ob-cart">
                                <li class="js--ob-no-items ob__results__no-items" v-if="productList.length < 1">
                                    <div class="ob__results__item">
                                        <p class="js--no-items">No products selected</p>
                                    </div>
                                </li>
                                <li v-for="product in productList" :key="product.Product.Id" class="ob__results_li">
                                    <div class="ob__results__item">
                                        <div class="ob__results__li ob--justify-start" data-label="Part #">{{ product.Product.PartNumber }}</div>
                                        <div class="ob__results__li ob--tc ob__results__center-result" data-label="Qty.">
                                            {{ product.Quantity }}&nbsp;&nbsp;&nbsp;
                                        </div>
                                        <div class="ob__results__li ob__results__center-result">&nbsp;</div>
                                        <div class="ob__results__li ob__results--flex3 ob--justify-start" data-label="Description">
                                            {{ product.Product.Description }}
                                        </div>
                                        <div class="ob__results__li" data-label="List">
                                            {{ product.Product.ListPrice | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Cost">
                                            {{ product.Product.ActualCost | formatMoney }}
                                        </div>
                                        <div class="ob__results__li" data-label="Total">
                                            {{ (product.Product.ActualCost * product.Quantity) | formatMoney }}
                                        </div>
                                        <div class="ob__results__li ob--justify-end">&nbsp;</div>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="summary-footer">
                            <span
                                >Total parts: <strong>{{ totalProductsQuantity }}</strong></span
                            >
                        </div>

                        <div class="step-button-container">
                            <span></span>
                            <div>
                                <button id="btnBackToStep3" class="button--border" @click="goToStep3">Back</button>
                                <button class="ob__checkout" @click="goToCheckout">Go to Checkout</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <transition name="modal" v-if="showProductModal">
            <div id="productModal" class="modal-mask" data-html2canvas-ignore="true">
                <div class="modal-wrapper">
                    <div class="modal-container">
                        <div class="modal-header">
                            <h3>Product List</h3>
                        </div>

                        <div class="modal-body">
                            <p>Select all the products you want to add to your order from the list below.</p>

                            <div class="list-container">
                                <input class="ob__search__input" placeholder="Search" v-model="productSearch" />

                                <div class="list-wrapper">
                                    <h4>Briggs & Stratton Branded Oil <span>Sell pack quantities will be applied as parts are added</span></h4>
                                    <ul class="list">
                                        <li v-for="product in briggsOilProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>

                                    <h4>Vanguard 2-Cycle and 4-Cycle Oil <span>Sell pack quantities will be applied as parts are added</span></h4>
                                    <ul class="list">
                                        <li v-for="product in vanguardOilProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>

                                    <h4>Air Filters</h4>
                                    <ul class="list">
                                        <li v-for="product in airFilterProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>

                                    <h4>Oil Filters</h4>
                                    <ul class="list">
                                        <li v-for="product in oilFilterProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>

                                    <h4>Fuel Filters</h4>
                                    <ul class="list">
                                        <li v-for="product in fuelFilterProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>

                                    <h4>Maintenance Kits</h4>
                                    <ul class="list">
                                        <li v-for="product in maintenanceKitProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>

                                    <h4>Engines</h4>
                                    <ul class="list">
                                        <li v-for="product in engineProducts" :key="product.Product.Id">
                                            <input
                                                type="checkbox"
                                                v-bind:value="product.Product.Id"
                                                v-model="checkedProductIds"
                                                name="product_data"
                                                class="js--input portal-module__module__checkbox"
                                            />
                                            <input
                                                type="number"
                                                name="product_quantity"
                                                placeholder="Qty"
                                                min="0"
                                                step="1"
                                                v-model.number="productQuantities[product.Product.Id]"
                                                :disabled="!checkedProductIds.includes(product.Product.Id)"
                                            />
                                            <div>
                                                {{ product.Product.PartNumber }}
                                                <span>{{ product.Product.Description }}</span>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <slot name="footer">
                                <button class="modal-skip-button" @click="closeProductModal">Cancel</button>
                                <button class="modal-submit-button" :disabled="isProcessing" @click="addSelectedProducts">Add products</button>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </section>
</template>
